import React from "react";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";

export function StatCard({ icon: Icon, label, value, hint, accent = "primary", testId, onClick }) {
  const accents = {
    primary: "text-primary bg-primary/10",
    secondary: "text-secondary bg-secondary/10",
    success: "text-[hsl(var(--success))] bg-[hsl(var(--success))]/10",
    warning: "text-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10",
    destructive: "text-destructive bg-destructive/10",
  };
  return (
    <div data-testid={testId} onClick={onClick}
      className={cn("bg-card border border-border rounded-md p-5 flex items-start justify-between transition-colors duration-150 hover:border-primary/40",
        onClick && "cursor-pointer hover:bg-muted/30")}>
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-3xl font-heading font-bold mt-2 tracking-tight">{value}</p>
        {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
      </div>
      {Icon && <div className={cn("h-11 w-11 rounded-md flex items-center justify-center", accents[accent])}><Icon className="h-5 w-5" /></div>}
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-4"><Icon className="h-6 w-6 text-muted-foreground" /></div>}
      <p className="text-sm text-muted-foreground mb-4">{title}</p>
      {action}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4">
          {Array.from({ length: cols }).map((_, j) => (
            <div key={j} className="h-8 bg-muted rounded animate-pulse flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function StatusBadge({ status }) {
  const active = status === "active";
  return (
    <span className={cn("inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium",
      active ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-muted text-muted-foreground")}>
      <span className={cn("h-1.5 w-1.5 rounded-full", active ? "bg-[hsl(var(--success))]" : "bg-muted-foreground")} />
      {status}
    </span>
  );
}
